<template>
  <BittsContainer class="role-card p-24">
    <div class="role-card__info">
      <div class="role-card__name-and-desc">
        <div class="flex items-center">
          <div class="role-card__name">
            {{ role.name }}
          </div>
          <div
            v-if="roleType"
            :class="{ 'role-card__type-custom': role.is_custom }"
            class="role-card__type"
          >
            {{ roleType }}
          </div>
        </div>
        <div class="role-card__desc">
          {{ role.description }}
        </div>
      </div>
      <div
        :class="{ 'role-card__members-num-none': hasNoMembers }"
        class="role-card__members-num"
      >
        {{ numRoleMembersText }}
      </div>
      <div class="role-card__edit">
        <BillingCTA
          v-if="isUpsell"
          size="x-small"
          button-text="Upgrade"
          :billing-interaction="{
            cta: TEAM_CTAS.ROLES,
            event_site: EVENT_SITES.INTEGRATION_BILLING_CTA,
            talkToSalesReason: 'Integration User role',
          }"
        />
        <template v-else>
          <BittsButton
            v-if="canEditRole"
            :text="editButtonText"
            type="neutral"
            size="x-small"
            @click="emit('edit-role')"
          />
          <BittsButton
            v-if="canShowRoleDetails"
            text="View"
            type="neutral"
            size="x-small"
            @click="emit('show-role')"
          />
        </template>
      </div>
    </div>
  </BittsContainer>
</template>

<script setup lang="ts">
import { BittsButton, BittsContainer } from '@crossbeam/bitts';
import { EVENT_SITES, TEAM_CTAS } from '@crossbeam/itly';

import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import BillingCTA from '@/components/billing/BillingCTA.vue';

import useAuth from '@/composables/useAuth';
import { useBillingStore } from '@/stores';
import { RoleWithIsCustom } from '@/types/roles';

const {
  numRoleMembersText = '',
  role = {
    id: '',
    name: '',
    description: '',
    is_editable: false,
    permissions: [],
    is_custom: false,
    seat_type: 'core',
  },
} = defineProps<{
  numRoleMembersText?: string;
  role?: RoleWithIsCustom;
}>();

const emit = defineEmits(['edit-role', 'show-role']);

const NO_MEMBERS = 'No members';

const { hasScope } = useAuth();
const billingStore = useBillingStore();

const { isSupernodeOrEnterpriseTier } = storeToRefs(billingStore);

const isIntegrationRole = computed(() => role.seat_type === 'integration');
const isUpsell = computed(
  () => isIntegrationRole.value && !isSupernodeOrEnterpriseTier.value,
);
const canEditRole = computed(() => hasScope('write:roles') && role.is_editable);
const canShowRoleDetails = computed(
  () => hasScope('write:roles') && !canEditRole.value,
);
const hasNoMembers = computed(() => numRoleMembersText === NO_MEMBERS);
const roleType = computed(() => {
  if (!role.is_editable) return 'System';
  if (role.is_custom) return 'Custom';
  return null;
});
const editButtonText = computed(() =>
  isSupernodeOrEnterpriseTier.value ? 'Edit' : 'View',
);
</script>

<style scoped lang="pcss">
@reference "../css/theme.css";
.role-card__desc {
  @apply text-neutral-text text-sm text-left max-w-[400px];
}
.role-card__edit {
  @apply flex items-center justify-end;
}
.role-card__info {
  @apply grid;
  grid-template-columns: 5fr 1fr 1fr;
}
.role-card__members-num {
  @apply text-neutral-text-weak text-sm flex items-center justify-start;
}
.role-card__members-num-none {
  @apply opacity-50;
}
.role-card__name {
  @apply text-neutral-text-strong text-m font-bold;
}
.role-card__name-and-desc {
  @apply flex flex-col items-start;
}
.role-card__type {
  @apply px-8 rounded-bts-md bg-neutral-background text-neutral-text text-sm ml-4;
}
.role-card__type-custom {
  @apply text-info-text bg-info-background-weak;
}
</style>
